import { useQuery } from '@tanstack/react-query';
import { Box, Grid } from '@mui/material';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import TodayIcon from '@mui/icons-material/TodayOutlined';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLateOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { getCounselorDashboard } from '../../api/dashboard';
import { useNewLeadStatusId } from '../../hooks/useNewLeadStatusId';
import { KpiCard, KpiGrid } from './KpiCard';
import { MyLeadTrendChart } from './MyLeadTrendChart';
import { ConversionCard } from './ConversionCard';
import { RecentLeadsCard } from './RecentLeadsCard';
import { MyRecentApplicationsCard } from './MyRecentApplicationsCard';
import { MyStudentPaymentsCard } from './MyStudentPaymentsCard';
import { MyFollowUpOverviewCard } from './MyFollowUpOverviewCard';
import { MyPersonalAppointmentsCard } from './MyPersonalAppointmentsCard';
import { MyPipelineCard } from './MyPipelineCard';
import { MyLeadSourcesCard } from './MyLeadSourcesCard';
import { PendingApprovalsCard } from './PendingApprovalsCard';
import { CounselorLeaderboardCard } from './CounselorLeaderboardCard';
import { DashboardLoadError } from './DashboardLoadError';

/** Counselor Dashboard — every widget here is scoped to the logged-in
 * Counselor's own activity by the backend (GET /dashboard/counselor),
 * never a company-wide total (spec §2/§17/§20). The one deliberate
 * exception is the Counselor Leaderboard at the bottom, kept exactly as
 * it already worked, per the spec's explicit "DO NOT CHANGE" instruction
 * (§1/§16). */
export const CounselorDashboardView = () => {
  const query = useQuery({ queryKey: ['counselor-dashboard'], queryFn: getCounselorDashboard });
  const d = query.data;
  // "My Pending Leads" click-through — the real "New" status id, so this
  // matches exactly what was counted (same shared pending-leads dataset
  // the Super Admin "Pending leads" KPI and "Pending Leads by Counselor"
  // now use too).
  const newStatusId = useNewLeadStatusId();

  return (
    <>
      {query.isError && <DashboardLoadError error={query.error} onRetry={() => void query.refetch()} />}
      <KpiGrid>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact label="My Leads" value={d?.myLeads ?? '—'} icon={ContactsIcon} color="#5B2DA3" to="/leads" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact label="Today's Leads" value={d?.todaysLeads ?? '—'} icon={TodayIcon} color="#2F6FED" to="/leads" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact
            label="My Follow-ups"
            value={d?.myFollowUpsDue ?? '—'}
            icon={AssignmentLateIcon}
            color="#F5A623"
            to="/leads"
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact label="My Students" value={d?.myStudents ?? '—'} icon={SchoolIcon} color="#12B76A" to="/students" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact
            label="My Applications"
            value={d?.myApplications ?? '—'}
            icon={AssignmentTurnedInIcon}
            color="#0BA5EC"
            to="/admissions"
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact label="My Payments" value={d?.myPayments ?? '—'} icon={PaymentsIcon} color="#12B76A" to="/payments" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact
            label="Pending Documents"
            value={d?.pendingDocuments ?? '—'}
            icon={DescriptionOutlinedIcon}
            color="#D92D20"
            to="/students?documents=pending"
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact
            label="Personal Appointments"
            value={
              d ? d.personalAppointments.today + d.personalAppointments.upcoming + d.personalAppointments.pendingConfirmation : '—'
            }
            icon={EventAvailableIcon}
            color="#8354C6"
            to="/personal-appointments"
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard compact
            label="My Pending Leads"
            value={d?.myPendingLeads ?? '—'}
            icon={HourglassEmptyIcon}
            color="#F5A623"
            to={newStatusId ? `/leads?leadStatusId=${newStatusId}` : '/leads?pending=true'}
          />
        </Box>
      </KpiGrid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={8}>
          <MyLeadTrendChart />
        </Grid>
        <Grid item xs={12} md={4}>
          <ConversionCard data={d?.conversion} title="My Conversion" />
        </Grid>

        <Grid item xs={12} md={6}>
          <RecentLeadsCard title="My Recent Leads" />
        </Grid>
        <Grid item xs={12} md={6}>
          <MyRecentApplicationsCard data={d?.recentApplications} loading={query.isLoading} />
        </Grid>

        <Grid item xs={12} md={6}>
          <MyStudentPaymentsCard data={d?.recentPayments} loading={query.isLoading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <MyFollowUpOverviewCard data={d?.followUpOverview} />
        </Grid>

        <Grid item xs={12} md={4}>
          <MyPersonalAppointmentsCard data={d?.personalAppointments} />
        </Grid>
        <Grid item xs={12} md={4}>
          <MyPipelineCard data={d?.pipeline} />
        </Grid>
        <Grid item xs={12} md={4}>
          <MyLeadSourcesCard data={d?.leadSources} loading={query.isLoading} />
        </Grid>

        <Grid item xs={12} md={6}>
          <PendingApprovalsCard title="My Pending Lead Approvals" />
        </Grid>
        <Grid item xs={12} md={6}>
          <CounselorLeaderboardCard />
        </Grid>
      </Grid>
    </>
  );
};
